/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnOrderState, RowData } from "@tanstack/react-table";

import { hasKeys, IColumnDef, VisibilityState } from "@ryde/shared";

export const isOutOfSort = <T extends RowData>(
  item: IColumnDef<T, any>,
): boolean => {
  const meta = item.meta;

  if (!meta) {
    return false;
  }

  const { fixed, orderable, last } = meta;
  return fixed || last || !orderable || false;
};

export const isOutOfSortReport = <T extends RowData>(
  item: IColumnDef<T, any>,
): boolean => {
  const meta = item.meta;

  if (!meta) {
    return false;
  }

  const { fixed, last } = meta;
  return fixed || last || false;
};

const isColumnVisible = (
  columnId: string,
  columnVisibility?: VisibilityState,
): boolean => {
  if (!columnVisibility || !hasKeys(columnVisibility)) {
    return true;
  }

  return columnVisibility[columnId] !== false;
};

export const getActiveItems = <T extends RowData>(
  items: IColumnDef<T, any>[],
  columnVisibility?: VisibilityState,
  columnOrder?: ColumnOrderState,
): IColumnDef<T, any>[] => {
  const activeItems: IColumnDef<T, any>[] = [];
  const activeIds = new Set<string>();
  const sortableItems = items.filter(item => !isOutOfSort(item) && item.id);

  if (columnOrder?.length) {
    columnOrder.forEach(id => {
      if (!isColumnVisible(id, columnVisibility)) {
        return;
      }

      const item = sortableItems.find(el => el.id === id);

      if (item?.id) {
        activeItems.push(item);
        activeIds.add(item.id);
      }
    });
  }

  sortableItems.forEach(column => {
    if (!column.id || activeIds.has(column.id)) {
      return;
    }

    if (
      !column.meta?.orderable ||
      isColumnVisible(column.id, columnVisibility)
    ) {
      activeItems.push(column);
    }
  });

  return activeItems;
};

export const getActiveItemsReports = <T extends RowData>(
  items: IColumnDef<T, any>[],
  columnVisibility?: VisibilityState,
  columnOrder?: ColumnOrderState,
): IColumnDef<T, any>[] => {
  const activeItems: IColumnDef<T, any>[] = [];
  const sortableItems = items.filter(item => !isOutOfSortReport(item));

  if (columnOrder?.length) {
    columnOrder.forEach(id => {
      if (columnVisibility && columnVisibility[id] !== false) {
        const item = sortableItems.find(el => el.id === id);

        if (item) {
          activeItems.push(item);
        }
      }
    });
  } else {
    sortableItems.forEach(column => {
      if (
        !column.meta?.orderable ||
        !columnVisibility ||
        (column.id && columnVisibility[column.id] !== false)
      ) {
        activeItems.push(column);
      }
    });
  }

  return activeItems;
};

export const getInactiveItems = <T extends RowData>(
  items: IColumnDef<T, unknown>[],
  columnVisibility?: VisibilityState,
): IColumnDef<T, unknown>[] => {
  const inactiveItems: IColumnDef<T, unknown>[] = [];
  const sortableItems = items.filter(item => !isOutOfSort(item));
  sortableItems.forEach(column => {
    if (
      columnVisibility &&
      column.id &&
      columnVisibility[column.id] === false
    ) {
      inactiveItems.push(column);
    }
  });
  return inactiveItems;
};

export const getInactiveItemsReports = <T extends RowData>(
  items: IColumnDef<T, unknown>[],
  columnVisibility?: VisibilityState,
): IColumnDef<T, unknown>[] => {
  const inactiveItems: IColumnDef<T, unknown>[] = [];
  const sortableItems = items.filter(item => !isOutOfSortReport(item));
  sortableItems.forEach(column => {
    if (
      columnVisibility &&
      column.id &&
      columnVisibility[column.id] === false
    ) {
      inactiveItems.push(column);
    }
  });
  return inactiveItems;
};

export const reorder = <T extends object>(
  list: IColumnDef<T, any>[],
  startIndex: number,
  endIndex: number,
): IColumnDef<T, any>[] => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};
